import { Link } from "react-router-dom";
import { HomeOpsVisual } from "../components/HomeOpsVisual";
import { experiences } from "../content/experience";

const engagements = experiences.map((item) => ({
  id: item.id,
  industry: item.industry,
  focus: item.highlights.find((h) => h.label === "Focus")?.value,
  term: item.highlights.find((h) => h.label === "Platform")?.value,
}));

const proof = [
  { term: "Architecture", body: "what gets built and in what order" },
  { term: "Production code", body: "we write the hard parts ourselves" },
  { term: "Verification", body: "tests and gates before a release" },
  { term: "Leadership", body: "clear status for the people shipping" },
] as const;

const capabilities = [
  {
    title: "Product engineering",
    terms: [
      "iOS",
      "Android",
      "Swift",
      "Metal",
      "React Native",
      "TypeScript",
      "App Store releases",
    ],
  },
  {
    title: "Cloud and platform",
    terms: [
      "AWS",
      "Node.js",
      "distributed services",
      "containers",
      "identity",
      "media storage",
      "push delivery",
    ],
  },
  {
    title: "Systems and edge",
    terms: [
      "embedded Linux",
      "OpenWrt",
      "firmware",
      "OTA updates",
      "reproducible builds",
      "device fleets",
      "hardware in the loop",
    ],
  },
  {
    title: "Quality engineering",
    terms: [
      "test strategy",
      "acceptance gates",
      "performance profiling",
      "visual regression",
      "private CI runners",
      "release automation",
      "rollback rehearsal",
    ],
  },
] as const;

const phases = [
  {
    code: "01",
    title: "Assess",
    body: "We look at the code, the pipeline, and how the team works. You get a short plan of what to fix first.",
  },
  {
    code: "02",
    title: "Engineer",
    body: "We design and build the parts that are blocking you, from apps down to device firmware when that is the problem.",
  },
  {
    code: "03",
    title: "Harden",
    body: "Tests, release automation, and notes the team can keep using after we leave.",
  },
] as const;

export function HomePage() {
  return (
    <>
      <section className="hero hero-split hero-home">
        <div className="hero-copy">
          <p className="eyebrow">Engineering consultancy</p>
          <h1>Special operations for critical systems</h1>
          <p className="lead">
            We take on software and device work where a bad release costs real
            money. Architecture, code, tests, and the people running the build.
          </p>
          <ul className="hero-proof">
            {proof.map((item) => (
              <li key={item.term}>
                <span className="hero-proof-term">{item.term}</span>
                <span className="hero-proof-body">{item.body}</span>
              </li>
            ))}
          </ul>
          <div className="hero-actions">
            <Link className="btn" to="/contact">
              Get in touch
            </Link>
            <Link className="btn btn-secondary" to="/services">
              Services
            </Link>
          </div>
          <div className="hero-record">
            <p className="hero-record-label">Recent engagements</p>
            <ul className="hero-record-list">
              {engagements.map((item) => (
                <li key={item.id}>
                  <p className="hero-record-head">
                    <span className="hero-record-industry">
                      {item.industry}
                    </span>
                    <span className="hero-record-term">{item.term}</span>
                  </p>
                  <p className="hero-record-focus">{item.focus}</p>
                </li>
              ))}
            </ul>
            <Link className="hero-record-link" to="/experience">
              Selected work
            </Link>
          </div>
        </div>
        <aside className="hero-ops-aside" aria-hidden>
          <HomeOpsVisual />
        </aside>
      </section>

      <section className="home-capabilities" aria-labelledby="home-cap-heading">
        <div className="home-section-head">
          <h2 id="home-cap-heading">Technical scope</h2>
          <p className="muted">
            We work across these. Most jobs touch more than one.
          </p>
        </div>
        <div className="capability-grid">
          {capabilities.map((cap) => (
            <article key={cap.title} className="capability-card">
              <h3>{cap.title}</h3>
              <ul className="tech-term-list">
                {cap.terms.map((term) => (
                  <li key={term}>
                    <span className="tech-term">{term}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="home-phases" aria-labelledby="home-phases-heading">
        <div className="home-section-head">
          <h2 id="home-phases-heading">Engagement model</h2>
          <p className="muted">
            Same shape whether you are stuck on an architecture call or a device
            in the field.
          </p>
        </div>
        <ol className="ops-phase-list">
          {phases.map((phase) => (
            <li key={phase.code} className="ops-phase">
              <span className="ops-phase-code">{phase.code}</span>
              <h3>{phase.title}</h3>
              <p className="muted">{phase.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="home-cta-strip">
        <p className="home-cta-text">
          Detail on <Link to="/services">Services</Link>. Past work on{" "}
          <Link to="/experience">Selected work</Link>.
        </p>
      </section>
    </>
  );
}
